import { useEffect, useMemo, useRef, useState } from "react";
import { useSyncedPlayer, type SyncedPlayerState } from "../player/useSyncedPlayer.ts";
import { masterUrl, subtitleUrl, VIDEO_BASE } from "../config.ts";
import { getMovie, watchLink } from "../catalog.ts";
import "./room.css";

export function Room({ roomId, movieId }: { roomId: string; movieId: string | null }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const src = useMemo(() => (movieId ? masterUrl(movieId) : null), [movieId]);
  const player = useSyncedPlayer(videoRef, roomId, src);
  const movie = getMovie(movieId);
  const subUrl = movieId ? subtitleUrl(movieId) : null;

  // Subtitles: on by default; a per-viewer timing nudge in case the file is off.
  const [subsOn, setSubsOn] = useState(true);
  const [subOffset, setSubOffset] = useState(0);
  const [subsReady, setSubsReady] = useState(false);

  const subtitleTracks = () => {
    const tt = videoRef.current?.textTracks;
    const out: TextTrack[] = [];
    if (tt) for (let i = 0; i < tt.length; i++) if (tt[i].kind === "subtitles" || tt[i].kind === "captions") out.push(tt[i]);
    return out;
  };

  // Reflect the on/off toggle onto the native text track (re-apply shortly after
  // mount since hls.js/browser may attach the track a beat late).
  useEffect(() => {
    const apply = () => subtitleTracks().forEach((t) => (t.mode = subsOn ? "showing" : "hidden"));
    apply();
    const t = window.setTimeout(apply, 600);
    return () => window.clearTimeout(t);
  }, [subsOn, movieId]);

  // The nudge buttons mutate loaded cues, so they only work once the VTT has
  // parsed. Poll briefly after mount and unlock the controls when cues appear —
  // otherwise a click before load would move the label but not the subtitles.
  useEffect(() => {
    setSubsReady(false);
    let ticks = 0;
    const iv = window.setInterval(() => {
      if (subtitleTracks().some((t) => (t.cues?.length ?? 0) > 0)) {
        setSubsReady(true);
        window.clearInterval(iv);
      } else if (++ticks > 40) {
        window.clearInterval(iv); // give up after ~20s
      }
    }, 500);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieId]);

  const shiftSubs = (delta: number) => {
    subtitleTracks().forEach((t) => {
      const cues = t.cues;
      if (!cues) return;
      for (let j = 0; j < cues.length; j++) {
        const c = cues[j] as VTTCue;
        c.startTime = Math.max(0, c.startTime + delta);
        c.endTime = Math.max(0, c.endTime + delta);
      }
    });
    setSubOffset((o) => Math.round((o + delta) * 10) / 10);
  };

  // The host arrives with ?host=1 (set when they start the party) → open the
  // invite sheet automatically, then clean the URL to the shareable link.
  const [inviteOpen, setInviteOpen] = useState(() => new URLSearchParams(window.location.search).has("host"));
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.has("host")) {
      p.delete("host");
      window.history.replaceState(null, "", `?${p.toString()}`);
    }
  }, []);

  const s = player.status;
  const waitingForPeer = s?.barrierHolding && (s?.peerBuffering || s?.selfBuffering);
  const link = movieId ? watchLink(roomId, movieId) : "";

  return (
    <div className="room">
      <header className="room-top">
        <a className="room-back" href={window.location.pathname} aria-label="Back to library">
          <span className="chev">‹</span> <span className="wm">MOV</span>
        </a>
        <div className="room-title">{movie ? movie.title : "Now playing"}</div>
        <div className="room-actions">
          <span className="presence">
            <span className={`pdot ${player.connected ? "on" : "off"}`} />
            {player.peerConnected ? "Watching together" : "Waiting for guest"}
          </span>
          <button className="invite-btn" onClick={() => setInviteOpen(true)}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Invite
          </button>
        </div>
      </header>

      <main className="room-stage">
        {!movieId ? (
          <div className="room-empty">
            <h2>No movie in this link</h2>
            <p>This watch-party link is missing a movie. Head back and start a new one.</p>
            <a className="btn-primary" href={window.location.pathname}>Back to library</a>
          </div>
        ) : (
          <div className="player-frame">
            <video ref={videoRef} className="video" controls playsInline preload="auto" poster={movie?.hero} crossOrigin="anonymous">
              {subUrl && <track kind="subtitles" src={subUrl} srcLang="en" label="English" default />}
            </video>
            {waitingForPeer && (
              <div className="ov">
                <div className="ov-spin" />
                <div className="ov-title">{s?.peerBuffering ? "Waiting for your guest" : "Buffering"}</div>
              </div>
            )}
            {player.watchdog && !waitingForPeer && (
              <div className="ov ov-soft">
                <div className="ov-title">Still buffering</div>
                <div className="ov-sub">Try a lower quality below, or keep waiting.</div>
              </div>
            )}
            {player.needsGesture && !waitingForPeer && (
              <button className="ov ov-tap" onClick={() => videoRef.current?.play().catch(() => {})}>
                <span className="tap-play">▶</span>
                <div className="ov-title">Tap to play</div>
              </button>
            )}
            {!VIDEO_BASE && (
              <div className="ov ov-soft">
                <div className="ov-title">Video source not set</div>
                <div className="ov-sub">Configure VITE_VIDEO_BASE to stream this title.</div>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="room-bar">
        <div className="rb-group">
          <span className="rb-label">Quality</span>
          <select
            className="rb-select"
            value={player.autoQuality ? -1 : player.selectedLevel}
            onChange={(e) => player.setLevel(Number(e.target.value))}
            aria-label="Playback quality"
          >
            <option value={-1}>Auto{player.currentHeight ? ` · ${player.currentHeight}p` : ""}</option>
            {player.levels
              .slice()
              .sort((a, b) => b.height - a.height)
              .map((l) => (
                <option key={l.index} value={l.index}>
                  {l.height}p
                </option>
              ))}
          </select>
        </div>
        {subUrl && (
          <div className="rb-group rb-subs">
            <button className={`cc-btn ${subsOn ? "on" : ""}`} onClick={() => setSubsOn((v) => !v)} aria-pressed={subsOn}>
              <span className="cc">CC</span> {subsOn ? "On" : "Off"}
            </button>
            {subsOn && (
              <span className="sub-offset">
                <button onClick={() => shiftSubs(-1)} disabled={!subsReady} title="Subtitles 1s earlier">−1s</button>
                <button onClick={() => shiftSubs(-0.5)} disabled={!subsReady} title="Subtitles 0.5s earlier">−½</button>
                <span className="off-val">{!subsReady ? "…" : subOffset === 0 ? "synced" : `${subOffset > 0 ? "+" : ""}${subOffset}s`}</span>
                <button onClick={() => shiftSubs(0.5)} disabled={!subsReady} title="Subtitles 0.5s later">+½</button>
                <button onClick={() => shiftSubs(1)} disabled={!subsReady} title="Subtitles 1s later">+1s</button>
              </span>
            )}
          </div>
        )}

        <SyncMeter status={s} />
        {player.isReference && <span className="rb-host">HOST</span>}
      </footer>

      {inviteOpen && <InviteSheet movieTitle={movie?.title ?? "this movie"} link={link} peerConnected={player.peerConnected} onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

function InviteSheet({
  movieTitle,
  link,
  peerConnected,
  onClose,
}: {
  movieTitle: string;
  link: string;
  peerConnected: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked */
    }
  };
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <button className="sheet-x" onClick={onClose} aria-label="Close">×</button>
        <div className="sheet-eyebrow">Watch party</div>
        <h2 className="sheet-title">Invite someone to {movieTitle}</h2>
        <p className="sheet-sub">Share this link. Whoever opens it watches with you.</p>
        <div className="sheet-link">
          <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Watch-party link" />
          <button className="btn-primary" onClick={copy}>{copied ? "Copied ✓" : "Copy link"}</button>
        </div>
        <div className={`sheet-status ${peerConnected ? "joined" : ""}`}>
          <span className="sheet-pulse" />
          {peerConnected ? "Your guest has joined — press play." : "Waiting for your guest to open the link…"}
        </div>
        <button className="sheet-done" onClick={onClose}>Start watching</button>
      </div>
    </div>
  );
}

function SyncMeter({ status }: { status: SyncedPlayerState["status"] }) {
  if (!status) return <div className="syncmeter">Establishing sync…</div>;
  const oneWay = Math.round(status.rttHalfMs);
  const label = oneWay < 60 ? "Locked" : oneWay < 150 ? "In sync" : "Loose sync";
  const color = oneWay < 60 ? "var(--ok)" : oneWay < 150 ? "var(--wait)" : "var(--wait-dim)";
  return (
    <div className="syncmeter">
      <span className="dot" style={{ background: color }} />
      <span>{status.barrierHolding ? "Holding for your guest" : label}</span>
      <span className="metric">· {oneWay}ms</span>
    </div>
  );
}
